<script setup>
import { computed, watch } from 'vue';
import { Handle, Position, useVueFlow, useNode } from '@vue-flow/core';
import { useI18n } from 'vue-i18n';
import { useNodeAudience } from '../../composables/useNodeAudience.js';

const props = defineProps({
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const { updateNodeInternals } = useVueFlow();
const { id } = useNode();
const audienceCount = useNodeAudience(id);

const cases = computed(() => props.data.cases ?? []);
const allHandles = computed(() => [...cases.value, { value: t('FLOW_BUILDER.NODES.SWITCH.DEFAULT'), handle: 'default' }]);

watch(allHandles, () => updateNodeInternals(id));

function handleLeft(index, total) {
  return `${((index + 1) / (total + 1)) * 100}%`;
}
</script>

<template>
  <div
    class="flow-node min-w-[160px] max-w-[220px] rounded-xl shadow-md border-2 transition-all"
    :class="selected ? 'border-orange-400' : 'border-transparent'"
  >
    <Handle type="target" :position="Position.Top" />

    <div class="flex items-center gap-2 px-3 py-2 bg-orange-500 text-white rounded-t-xl">
      <span class="i-lucide-git-fork size-4 shrink-0" />
      <span class="text-xs font-semibold truncate flex-1">{{ data.name || t('FLOW_BUILDER.NODES.SWITCH.TITLE') }}</span>
      <span v-if="audienceCount !== null" class="text-[9px] bg-white/20 rounded-full px-1.5 py-0.5 font-medium tabular-nums shrink-0">
        {{ audienceCount }}
      </span>
    </div>

    <div class="px-3 py-2 bg-n-solid-1 text-n-slate-12 text-xs truncate" :class="allHandles.length ? '' : 'rounded-b-xl'">
      {{ data.field || t('FLOW_BUILDER.NODES.SWITCH.SELECT_FIELD') }}
    </div>

    <div
      v-if="allHandles.length"
      class="relative bg-n-solid-1 rounded-b-xl pb-3 pt-1"
      :style="{ minHeight: '28px' }"
    >
      <span
        v-for="(c, i) in allHandles"
        :key="c.handle || i"
        class="absolute text-[9px] text-n-slate-9 truncate max-w-[60px] text-center -translate-x-1/2"
        :style="{ left: handleLeft(i, allHandles.length), bottom: '10px' }"
      >
        {{ c.value }}
      </span>
    </div>

    <Handle
      v-for="(c, i) in allHandles"
      :id="c.handle || 'default'"
      :key="c.handle || i"
      type="source"
      :position="Position.Bottom"
      class="!bg-orange-500 !border-orange-400"
      :style="{ left: handleLeft(i, allHandles.length) }"
    />
  </div>
</template>
